import React from 'react';
import { CARD_STYLES, BUTTON_STYLES } from '../../constants/styles';
import { ValidationWarning } from '../Messages/StatusMessages';

const CompactMealCard = ({ meal, actualIndex, onOpenModal, onQuantityChange, isLocked, saving }) => {
    // Validation helper functions
    const warningProteinCount = () => {
        if (!meal.proteinOptions || !Array.isArray(meal.proteinOptions)) return null;
        return meal.proteinOptions.filter(option => option.isActive).length < 1 && meal.proteinOptions.length > 0;
    };

    const warningVeggieCount = () => {
        if (!meal.veggieOptions || !Array.isArray(meal.veggieOptions)) return null;
        return meal.veggieOptions.filter(option => option.isActive).length < 1 && meal.veggieOptions.length > 0;
    };

    const warningStarchCount = () => {
        if (!meal.starchOptions || !Array.isArray(meal.starchOptions)) return null;
        return meal.starchOptions.filter(option => option.isActive).length < 1 && meal.starchOptions.length > 0;
    };

    const warningSauceCount = () => {
        if (!meal.sauceOptions || !Array.isArray(meal.sauceOptions) || meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') return null;
        return meal.sauceOptions.filter(option => option.isActive).length < 1 && meal.sauceOptions.length > 0;
    };

    // Check if any validation warnings exist
    const hasValidationWarnings = meal.meal !== 'Snack' && meal.quantity > 0 && (
        warningProteinCount() ||
        warningVeggieCount() ||
        warningStarchCount() ||
        warningSauceCount()
    );

    const getValidationMessages = () => {
        const messages = [];
        if (meal.meal !== 'Snack' && meal.meal !== 'Add On' && meal.quantity > 0) {
            if (warningProteinCount()) messages.push('No Protein Selected');
            if (warningVeggieCount()) messages.push('No Veggie Selected');
            if (warningStarchCount()) messages.push('No Starch Selected');
            if (warningSauceCount()) messages.push('No Sauce Selected');
        }
        return messages;
    };

    return (
        <div
            onClick={() => onOpenModal(meal, actualIndex)}
            style={{
                ...CARD_STYLES.container(meal),
                border: hasValidationWarnings ? '2px solid #e74c3c' : CARD_STYLES.container(meal).border,
                boxShadow: hasValidationWarnings
                    ? '0 2px 10px rgba(231, 76, 60, 0.2)'
                    : CARD_STYLES.container(meal).boxShadow
            }}
        >
            {meal.imageUrl && (
                <div
                    style={{
                        ...CARD_STYLES.image,
                        backgroundImage: `url(${meal.imageUrl})`,
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                    }}
                />
            )}

            {/* Add temporary indicator */}
            {meal.isTemporary && (
                <div style={{
                    fontSize: '14px',
                    color: '#856404',
                    backgroundColor: 'solid #fff3cd',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    marginBottom: '4px',
                    textAlign: 'center'
                }}>
                    Saving...
                </div>
            )}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Title and quantity controls */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    flexWrap: 'wrap',
                    gap: '4px'
                }}>
                    <div style={CARD_STYLES.title}>
                        {meal.itemName}
                        {meal.quantity === 0 && (
                            <span style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontWeight: 'normal',
                                border: '1px solid #1976d2'
                            }}>
                                Available
                            </span>
                        )}
                    </div>

                    {/* Edit indicator */}
                    {meal.quantity > 0 && (
                        <div style={{
                            fontSize: '13px',
                            color: '#007bff',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            pointerEvents: 'none',
                            flexShrink: 0
                        }}>
                            EDIT
                        </div>
                    )}
                </div>

                {/* Ingredient list at bottom */}
                <div style={{
                    fontSize: '10px',
                    color: '#666',
                    marginTop: 'auto',
                    lineHeight: '1.2'
                }}>
                    {meal.finalIngredients && meal.finalIngredients.length > 0
                        ? meal.finalIngredients.map(ingredient => (ingredient?.name || '').trim()).filter(Boolean).join(', ')
                        : meal.originalIngredients?.map(ingredient => (ingredient?.name || '').trim()).filter(Boolean).join(', ') || ''
                    }
                </div>

                {/* Validation warnings */}
                {hasValidationWarnings && !meal.isTemporary && (
                    <ValidationWarning message={getValidationMessages().join(', ')} />
                )}
                {(meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') && meal.price > 0 &&
                    <span style={{ fontSize: '10px', color: '#666', marginTop: '10px' }}>
                        Price: ${meal.price}
                    </span>}

                {/* Quantity display at bottom */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' }}>
                    <span style={{ fontSize: '10px', color: '#666' }}>
                        Qty: {meal.quantity}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', paddingTop: '10px' }}>
                        {meal.quantity > 0 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onQuantityChange(actualIndex, Math.max(0, meal.quantity - 1));
                                }}
                                disabled={isLocked || saving || meal.quantity <= 0}
                                style={{
                                    ...BUTTON_STYLES.quantity.remove,
                                    cursor: (isLocked || saving || meal.quantity <= 0) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Remove
                            </button>
                        )}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onQuantityChange(actualIndex, meal.quantity + 1);
                            }}
                            disabled={isLocked || saving}
                            style={{
                                ...BUTTON_STYLES.quantity.add,
                                cursor: (isLocked || saving) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Add
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompactMealCard;